import {Filter} from '@github-ui/filter'
import {LABELS} from '@github-ui/issues-shared/labels'
import {type EditableShortcutField, ShortcutEditingHeader, type UserShortcut} from '@github-ui/saved-views'
import {useCallback} from 'react'

import {SavedViewMutationErrorBanner} from '../../components/views/SavedViewMutationErrorBanner'
import type {SavedView} from '../../components/views/types'
import {SAVED_VIEW_UPDATE_SOURCES, useSavedViewAnalytics} from '../../hooks/use-saved-view-analytics'
import {useSavedViewFilterProviders} from '../../hooks/use-saved-view-filter-providers'
import {useUpdateSavedViewMutation} from '../../hooks/use-update-saved-view-mutation'
import styles from './SavedViewEditHeader.module.css'

interface SavedViewEditHeaderProps {
  savedView: SavedView
  owner: string
  repo: string
  shortcut: UserShortcut
  setShortcutValue: (field: EditableShortcutField, value: string) => void
  onPreviewQuerySubmit: (next: string) => void
  onSaved: () => void
  onCancel: () => void
}

export function SavedViewEditHeader({
  savedView,
  owner,
  repo,
  shortcut,
  setShortcutValue,
  onPreviewQuerySubmit,
  onSaved,
  onCancel,
}: SavedViewEditHeaderProps) {
  const filterProviders = useSavedViewFilterProviders({owner, repo})

  const {mutate, isPending, error} = useUpdateSavedViewMutation({owner, repo})
  const {trackViewUpdated} = useSavedViewAnalytics()

  const handleSave = useCallback(() => {
    mutate(
      {
        id: savedView.id,
        name: shortcut.name.trim(),
        query: shortcut.query.trim(),
        description: shortcut.description?.trim() ?? '',
        icon: shortcut.icon,
        color: shortcut.color,
      },
      {
        onSuccess: () => {
          trackViewUpdated(savedView.id, SAVED_VIEW_UPDATE_SOURCES.edit)
          onSaved()
        },
      },
    )
  }, [mutate, savedView.id, shortcut, onSaved, trackViewUpdated])

  return (
    <div className={styles.editHeaderWrapper}>
      <SavedViewMutationErrorBanner
        message={error ? LABELS.views.updateError : null}
        onRetry={handleSave}
        isRetrying={isPending}
      />
      <ShortcutEditingHeader
        shortcut={shortcut}
        setShortcutValue={setShortcutValue}
        onSave={handleSave}
        onDismiss={onCancel}
        isSaving={isPending}
        renderFilter={({inputRef, className}) => (
          <Filter
            id="saved-view-edit-filter"
            className={className}
            inputRef={inputRef}
            label={LABELS.views.filterQueryLabel}
            placeholder={LABELS.views.filterQueryPlaceholder}
            variant="input"
            providers={filterProviders}
            filterValue={shortcut.query}
            onChange={value => setShortcutValue('query', value)}
            onSubmit={request => onPreviewQuerySubmit(request.raw)}
            settings={{aliasMatching: true, groupAndKeywordSupport: true}}
          />
        )}
      />
    </div>
  )
}
